import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createDefaultConfig, DEFAULT_MATCH_STEPS, durationTicksForSteps, AI_DECISION_INTERVAL_TICKS } from "@cc/engine";
import { emptyDraft, toAiConfig, draftFromConfig } from "./AiPanel";
import type { AiDraft } from "./AiPanel";
import { ArenaCanvas } from "./ArenaCanvas";
import { Hud } from "./Hud";
import { ResultOverlay } from "./ResultOverlay";
import { BattleTimeline, SplitTimelines } from "./Timeline";
import { Rules } from "./Rules";
import { RulesStrip } from "./RulesStrip";
import { StartDuelModal, defaultSideDraft } from "./StartDuelModal";
import type { SideDraft } from "./StartDuelModal";
import { VisionPanel } from "./VisionPanel";
import { Replay } from "./Replay";
import {
  AI_CRICKET_A_ID,
  AI_CRICKET_B_ID,
  CRICKETS,
  findCricket,
  getAiConfig,
  hasAiConfig,
  isAiCricketId,
  isKnownCricketId,
  resolveAiId,
  setAiConfig,
  type AiCricketId,
} from "./crickets";
import { useRoute, navigate } from "./router";
import type { Locale, Dict } from "./i18n";
import { detectInitialLocale, dict, personaName, persistLocale } from "./i18n";
import { MatchClient } from "./match-client";
import { makeMatchId } from "./match-id";

type Speed = 1 | 2 | 4;

interface Setup {
  readonly left: string;
  readonly right: string;
  readonly seed: number;
  readonly seconds: number;
}

function makeSetup(left: string, right: string, seconds: number): Setup {
  return {
    left,
    right,
    seconds,
    seed: Math.floor(Math.random() * 1_000_000),
  };
}

/** Reads a match out of the URL, so any match is a shareable link. */
function setupFromUrl(): Setup {
  const params = new URLSearchParams(window.location.search);
  const known = (value: string | null, fallback: string) => {
    if (!value) return fallback;
    const id = resolveAiId(value);
    // AI ids are only valid once a session key exists; otherwise fall back so
    // an old share link does not crash the page on open.
    if (isAiCricketId(id)) return hasAiConfig(id as AiCricketId) ? id : fallback;
    return isKnownCricketId(id) ? id : fallback;
  };

  const left = known(params.get("a"), "collector");
  let right = known(params.get("b"), "hunter");
  if (right === left) right = CRICKETS.find((c) => c.id !== left)?.id ?? "hunter";

  const secondsRaw = Number(params.get("seconds") ?? params.get("steps"));
  // Old builds defaulted to 300 and wrote that into the share URL. Treat a bare
  // 300 as the stale default so refreshing an old tab picks up the new length.
  // `seconds` in the URL means abstract decision steps (one step per abstract
  // second), not wall-clock time.
  const seconds =
    Number.isFinite(secondsRaw) &&
    secondsRaw >= 10 &&
    secondsRaw <= 900 &&
    Math.round(secondsRaw) !== 300
      ? Math.round(secondsRaw)
      : DEFAULT_MATCH_STEPS;

  const seedRaw = Number(params.get("seed"));
  const base = makeSetup(left, right, seconds);
  return Number.isFinite(seedRaw) && params.has("seed")
    ? { ...base, seed: Math.abs(Math.trunc(seedRaw)) }
    : base;
}

function sideFromSetupId(id: string): SideDraft {
  if (isAiCricketId(id)) return { kind: "ai", botId: "collector" };
  return defaultSideDraft(id);
}

export default function App() {
  const route = useRoute();
  const [locale, setLocale] = useState<Locale>(() => detectInitialLocale().locale);
  const d = useMemo(() => dict(locale), [locale]);

  useEffect(() => {
    persistLocale(locale);
  }, [locale]);

  return (
    <div className="app">
      <div className="topbar">
        <div className="brand">
          <h1>CYBER CRICKET</h1>
          <span className="tagline">{d.tagline}</span>
        </div>
        <nav className="nav">
          <button
            className={route.name === "live" || route.name === "replay" ? "nav-on" : ""}
            onClick={() => navigate({ name: "live" })}
          >
            {d.navLive}
          </button>
          <button
            className={route.name === "rules" ? "nav-on" : ""}
            onClick={() => navigate({ name: "rules" })}
          >
            {d.navRules}
          </button>
        </nav>
        <div className="controls">
          <button
            className="ghost"
            title={locale === "en" ? "切换为中文" : "Switch to English"}
            onClick={() => setLocale((l) => (l === "en" ? "zh" : "en"))}
          >
            {d.languageName}
          </button>
        </div>
      </div>

      {route.name === "rules" ? <Rules locale={locale} d={d} /> : null}
      {route.name === "replay" ? <Replay id={route.id} locale={locale} d={d} /> : null}
      {route.name === "live" || route.name === "crickets" || route.name === "create" ? (
        <Arena locale={locale} d={d} />
      ) : null}
    </div>
  );
}

/**
 * The live arena.
 *
 * Laid out as one screen rather than a stack of full-width panels, because the
 * product is a *spectator* experience: everything needed to understand a match
 * should be visible at once, without scrolling, or the viewer reads the page
 * instead of watching the fight.
 *
 * Two columns under the chrome:
 *
 *   1. The main stage - HUD, then a three-cell grid (A's vision+timeline,
 *      broadcast canvas, B's vision+timeline), then transport controls.
 *   2. A narrow rules strip on the right of the battle zone - glanceable
 *      reference that does not compete with the fight.
 *
 * Starting a duel opens a modal where both sides are chosen: each can be a
 * built-in rule bot or a live AI cricket (including AI vs AI). AI configs are
 * held in `crickets.ts` as session-only module state.
 */
function Arena({ locale, d }: { locale: Locale; d: Dict }) {
  const [setup, setSetup] = useState<Setup>(() => setupFromUrl());
  const [client, setClient] = useState<MatchClient | null>(null);
  const [speed, setSpeed] = useState<Speed>(1);
  const [paused, setPaused] = useState(false);
  const [tickView, setTickView] = useState(0);
  const [showResult, setShowResult] = useState(true);
  const [leftSide, setLeftSide] = useState<SideDraft>(() => sideFromSetupId(setupFromUrl().left));
  const [rightSide, setRightSide] = useState<SideDraft>(() => sideFromSetupId(setupFromUrl().right));
  const [leftDraft, setLeftDraft] = useState<AiDraft>(() => emptyDraft());
  const [rightDraft, setRightDraft] = useState<AiDraft>(() => emptyDraft());
  const [startError, setStartError] = useState<string | null>(null);
  const [degraded, setDegraded] = useState<readonly number[]>([]);
  const [waiting, setWaiting] = useState<readonly number[]>([]);
  const [showDuelModal, setShowDuelModal] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("duel") === "1";
  });
  /** Once the user dismisses the settlement modal, do not force it back open. */
  const resultAutoShown = useRef(false);

  // Drop the one-shot ?duel=1 flag once consumed so a refresh does not reopen it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("duel") !== "1") return;
    params.delete("duel");
    const qs = params.toString();
    window.history.replaceState(null, "", qs ? `/?${qs}` : "/");
  }, []);

  /**
   * Whose eyes each vision inset looks through is *positional*, not a mode.
   *
   * The left rail always shows slot 0 and the right rail always shows slot 1. An
   * earlier version had a single inset with a viewpoint toggle, and it failed for
   * a reason worth recording: with one panel, the viewer has to read a label to
   * know whose eyes they are looking through, and a masked view that cannot be
   * identified at a glance is indistinguishable from a broken renderer. Two
   * fixed panes on the two sides make the question answer itself.
   */

  useEffect(() => {
    let next: MatchClient;
    try {
      const left = findCricket(setup.left);
      const right = findCricket(setup.right);
      const liveAi = isAiCricketId(left.id) || isAiCricketId(right.id);
      const interval = liveAi ? AI_DECISION_INTERVAL_TICKS : undefined;
      const config = createDefaultConfig({
        seed: setup.seed,
        durationTicks: durationTicksForSteps(setup.seconds, interval),
        agentIds: [left.id, right.id],
        // Live models share the same decision grid as bots (one step = one
        // abstract second). Aligned phases + world-hold mean both reason about
        // the same board; latency never eats arena steps.
        ...(liveAi
          ? { decisionIntervalTicks: AI_DECISION_INTERVAL_TICKS, alignedDecisions: true }
          : {}),
      });
      next = new MatchClient(
        config,
        [left.create(), right.create()],
        [
          { slot: 0, id: left.id, persona: left.persona, model: left.model, icon: left.icon },
          { slot: 1, id: right.id, persona: right.persona, model: right.model, icon: right.icon },
        ],
      );
      setStartError(null);
    } catch (err) {
      // Most likely a missing key for the AI cricket. Say so rather than
      // silently substituting a bot, which would make the match a lie.
      setStartError(err instanceof Error ? err.message : String(err));
      return;
    }

    next.setLocale(locale);
    setClient(next);
    setShowResult(true);
    resultAutoShown.current = false;
    setPaused(false);
    setTickView(0);

    const params = new URLSearchParams({
      a: setup.left,
      b: setup.right,
      seed: String(setup.seed),
      seconds: String(DEFAULT_MATCH_STEPS),
      lang: locale,
    });
    window.history.replaceState(null, "", `/?${params.toString()}`);
    // `locale` is intentionally excluded: a language change must not restart the
    // match. It is applied to the existing client by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setup]);

  // Language changes retarget the live match in place. Because timeline entries
  // store facts rather than prose, the whole history relabels without a restart.
  useEffect(() => {
    if (!client) return;
    client.setLocale(locale);
  }, [client, locale]);

  useEffect(() => {
    if (!client) return;
    const id = window.setInterval(() => {
      setTickView(client.tick);
      setDegraded(client.degradedSlots());
      // Waiting can change without the tick advancing (model still thinking),
      // so poll it explicitly or the "thinking…" badge would stick / never appear.
      setWaiting(client.waitingSlots());
      // Auto-open settlement once when the match first finishes — never force it
      // back open after the user dismisses via "查看详情".
      if (client.finished && client.getResult() && !resultAutoShown.current) {
        resultAutoShown.current = true;
        setShowResult(true);
      }
    }, 250);
    return () => window.clearInterval(id);
  }, [client]);

  const openDuelModal = useCallback(() => {
    setLeftSide(sideFromSetupId(setup.left));
    setRightSide(sideFromSetupId(setup.right));
    const leftCfg = isAiCricketId(setup.left)
      ? getAiConfig(resolveAiId(setup.left) as AiCricketId)
      : null;
    const rightCfg = isAiCricketId(setup.right)
      ? getAiConfig(resolveAiId(setup.right) as AiCricketId)
      : null;
    if (leftCfg) setLeftDraft(draftFromConfig(leftCfg));
    if (rightCfg) setRightDraft(draftFromConfig(rightCfg));
    setShowDuelModal(true);
  }, [setup.left, setup.right]);

  const pick = (side: 0 | 1, id: string) => {
    setSetup((s) => {
      const next = { ...s };
      if (side === 0) next.left = id;
      else next.right = id;
      // Two identical crickets make a dull mirror match, so swap the opponent.
      if (next.left === next.right) {
        const other =
          CRICKETS.find((c) => c.id !== id)?.id ??
          (id === AI_CRICKET_A_ID ? AI_CRICKET_B_ID : AI_CRICKET_A_ID);
        if (side === 0) next.right = other;
        else next.left = other;
      }
      return next;
    });
  };

  const clearAiFromSetup = () => {
    setAiConfig(null);
    setSetup((s) => ({
      ...s,
      left: isAiCricketId(s.left) ? "collector" : s.left,
      right: isAiCricketId(s.right) ? "hunter" : s.right,
    }));
  };

  /** Commit both sides from the duel modal and start a fresh match. */
  const startDuel = () => {
    setAiConfig(null);
    const leftId = leftSide.kind === "ai" ? AI_CRICKET_A_ID : leftSide.botId;
    const rightId = rightSide.kind === "ai" ? AI_CRICKET_B_ID : rightSide.botId;
    if (leftSide.kind === "ai") setAiConfig(AI_CRICKET_A_ID, toAiConfig(leftDraft, "AI A"));
    if (rightSide.kind === "ai") setAiConfig(AI_CRICKET_B_ID, toAiConfig(rightDraft, "AI B"));
    setSetup(makeSetup(leftId, rightId, DEFAULT_MATCH_STEPS));
    setShowDuelModal(false);
  };

  /** Same fighters / AI configs, new seed. */
  const rematchSameConfig = () => {
    setShowResult(false);
    setSetup(makeSetup(setup.left, setup.right, setup.seconds));
  };

  const openNewDuel = () => {
    setShowResult(false);
    openDuelModal();
  };

  const viewResultDetails = () => {
    setShowResult(false);
    // Reveal the already-rendered battle timeline under the arena.
    window.requestAnimationFrame(() => {
      document.getElementById("battle-timeline")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const outcome = client?.getResult() ?? null;

  return (
    <div className="arena-layout">
      <div className="arena-main">
        {/* Row 1: who is winning, and the controls to change who is playing.
            Info for each side sits above its HP bar in the HUD. */}
        {client ? (
          <Hud client={client} locale={locale} d={d} onPick={pick} waitingSlots={waiting} />
        ) : null}

        {/* Broadcast in the centre; each cricket's vision + timeline in the
            gutters on that cricket's side. Position answers "whose eyes?". */}
        <div className="arena-grid">
          <div className="arena-rail arena-rail-left">
            {client ? (
              <VisionPanel client={client} locale={locale} d={d} slot={0} />
            ) : null}
            {client ? (
              <SplitTimelines slot={0} client={client} locale={locale} d={d} />
            ) : null}
          </div>

          <div className="arena-center">
            {client ? (
              <ArenaCanvas client={client} speed={speed} paused={paused} d={d} />
            ) : null}
          </div>

          <div className="arena-rail arena-rail-right">
            {client ? (
              <VisionPanel client={client} locale={locale} d={d} slot={1} />
            ) : null}
            {client ? (
              <SplitTimelines slot={1} client={client} locale={locale} d={d} />
            ) : null}
          </div>
        </div>

        <div className="control-row">
          <button className={paused ? "active" : ""} onClick={() => setPaused((p) => !p)}>
            {paused ? d.resume : d.pause}
          </button>
          {([1, 2, 4] as const).map((s) => (
            <button key={s} className={speed === s ? "active" : ""} onClick={() => setSpeed(s)}>
              {s}×
            </button>
          ))}
          <button className="primary" onClick={openDuelModal}>
            {d.newMatch}
          </button>
          <span className="sep" />
          <button
            className="ghost"
            onClick={() => {
              const id = makeMatchId([setup.left, setup.right], setup.seed);
              navigate({ name: "replay", id });
            }}
          >
            {d.replayTitle}
          </button>
          <span className="sep" />
          <button className="ghost" onClick={() => navigate({ name: "rules" })}>
            {d.navRules}
          </button>
        </div>

        {client ? (
          <div className="scrubber">
            <span className="mono scrub-time">
              {Math.floor(tickView / 10)}
            </span>
            <input
              type="range"
              min={0}
              max={Math.max(1, tickView)}
              value={Math.max(0, Math.min(tickView, client.tick))}
              onChange={(e) => {
                const target = Number(e.target.value);
                setPaused(true);
                client.seekTo(target);
                setTickView(target);
              }}
              disabled={!client.canSeek}
            />
            <span className="mono scrub-time">
              {Math.floor(client.config.durationTicks / 10)}
            </span>
          </div>
        ) : null}

        {startError ? (
          <div className="start-error">
            <strong>{d.aiActive}</strong>
            <p>{startError}</p>
            <button
              className="ghost"
              onClick={() => {
                clearAiFromSetup();
                setShowDuelModal(true);
              }}
            >
              {d.aiDisable}
            </button>
          </div>
        ) : null}

        {client && degraded.length > 0 ? (
          <div className="start-error degraded">
            <strong>
              {d.aiDegraded(personaName(client.personaOf(client.infos[degraded[0]!]!.id), locale))}
            </strong>
            <p>{d.aiDegradedHint}</p>
          </div>
        ) : null}

        {client && outcome ? <BattleTimeline client={client} locale={locale} d={d} /> : null}
      </div>

      {/* Narrow rules column beside the battle zone — reference, not a page. */}
      <RulesStrip locale={locale} d={d} />

      {showDuelModal ? (
        <StartDuelModal
          d={d}
          left={leftSide}
          right={rightSide}
          leftDraft={leftDraft}
          rightDraft={rightDraft}
          onChangeLeft={setLeftSide}
          onChangeRight={setRightSide}
          onChangeLeftDraft={setLeftDraft}
          onChangeRightDraft={setRightDraft}
          onStart={startDuel}
          onClose={() => setShowDuelModal(false)}
        />
      ) : null}

      {client && outcome && showResult ? (
        <ResultOverlay
          outcome={outcome}
          client={client}
          locale={locale}
          d={d}
          onRematch={rematchSameConfig}
          onNewDuel={openNewDuel}
          onViewDetails={viewResultDetails}
        />
      ) : null}
    </div>
  );
}
